import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  X,
  User,
  Clock,
  MessageSquare,
  Send,
  Timer,
  AlertCircle,
  RefreshCw,
  Plus,
  Trash2,
  Headphones,
} from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import SelectField from "../ui/SelectField";
import InputField from "../ui/InputField";
import TextAreaField from "../ui/TextAreaField";

const API = process.env.REACT_APP_API_URL;

const PRIORIDAD_COLOR = {
  Baja: "bg-green-100 text-green-700",
  Media: "bg-yellow-100 text-yellow-700",
  Alta: "bg-orange-100 text-orange-700",
  Crítica: "bg-red-100 text-red-700",
};

const ESTADO_COLOR = {
  Nuevo: "bg-blue-500",
  "En revisión": "bg-purple-500",
  "En atención": "bg-indigo-500",
  "En espera cliente": "bg-yellow-500",
  Resuelto: "bg-green-500",
  Cerrado: "bg-gray-400",
  Cancelado: "bg-red-400",
};

const ESTADOS = [
  "Nuevo",
  "En revisión",
  "En atención",
  "En espera cliente",
  "Resuelto",
  "Cerrado",
  "Cancelado",
];
const TIPOS_ACTIVIDAD = [
  "Diagnóstico",
  "Atención remota",
  "Visita presencial",
  "Capacitación",
  "Gestión administrativa",
  "Otro",
];
const TIPOS_COMENTARIO = [
  { _id: "interno", name: "Nota interna" },
  { _id: "cliente", name: "Visible al cliente" },
];

export default function SoporteDetailModal({ ticketId, onClose }) {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bitacora");

  // Bitácora
  const [comentario, setComentario] = useState("");
  const [tipoComent, setTipoComent] = useState("interno");
  const [enviando, setEnviando] = useState(false);

  // Time entry
  const [timeForm, setTimeForm] = useState({
    minutos: "",
    tipoActividad: "Diagnóstico",
    observacion: "",
  });
  const [registrandoTiempo, setRegistrandoTiempo] = useState(false);

  // Usuarios para asignación
  const [usuarios, setUsuarios] = useState([]);
  const [nuevoEstado, setNuevoEstado] = useState("");
  const [nuevoResponsable, setNuevoResponsable] = useState("");

  const fetchTicket = async () => {
    try {
      const { data } = await axios.get(`${API}/soporte/${ticketId}`, {
        headers,
      });
      setTicket(data);
      setNuevoEstado(data.estado);
      setNuevoResponsable(data.responsable?._id || "");
    } catch (err) {
      toast.error("Error al cargar ticket");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
    // eslint-disable-next-line
  }, [ticketId]);

  useEffect(() => {
    if (hasPermission("soporte.asignar")) {
      axios
        .get(`${API}/users`, { headers })
        .then(({ data }) => setUsuarios(data))
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const handleCambiarEstado = async () => {
    if (!nuevoEstado) return toast.warning("Selecciona un estado");
    if (nuevoEstado === ticket.estado) return;
    try {
      await axios.put(
        `${API}/soporte/${ticketId}/estado`,
        { estado: nuevoEstado },
        { headers },
      );
      toast.success("Estado actualizado");
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const handleAsignarResponsable = async () => {
    try {
      await axios.put(
        `${API}/soporte/${ticketId}/asignar`,
        { responsable: nuevoResponsable || null },
        { headers },
      );
      toast.success("Responsable asignado");
      fetchTicket();
    } catch (err) {
      toast.error("Error al asignar");
    }
  };

  const handleComentario = async (e) => {
    e.preventDefault();
    if (!comentario.trim()) return;
    setEnviando(true);
    try {
      await axios.post(
        `${API}/soporte/${ticketId}/comentario`,
        { texto: comentario, tipo: tipoComent },
        { headers },
      );
      setComentario("");
      toast.success("Comentario agregado");
      fetchTicket();
    } catch (err) {
      toast.error("Error al agregar comentario");
    } finally {
      setEnviando(false);
    }
  };

  const handleTimeEntry = async (e) => {
    e.preventDefault();
    if (!timeForm.minutos || Number(timeForm.minutos) <= 0) {
      return toast.error("Ingresa los minutos de trabajo");
    }
    setRegistrandoTiempo(true);
    try {
      await axios.post(
        `${API}/soporte/${ticketId}/tiempo`,
        {
          minutos: Number(timeForm.minutos),
          tipoActividad: timeForm.tipoActividad,
          observacion: timeForm.observacion,
        },
        { headers },
      );
      setTimeForm({
        minutos: "",
        tipoActividad: "Diagnóstico",
        observacion: "",
      });
      toast.success("Tiempo registrado");
      fetchTicket();
    } catch (err) {
      toast.error("Error al registrar tiempo");
    } finally {
      setRegistrandoTiempo(false);
    }
  };

  const handleEliminarEntry = async (entryId) => {
    if (!window.confirm("¿Eliminar este registro de tiempo?")) return;
    try {
      await axios.delete(`${API}/soporte/${ticketId}/tiempo/${entryId}`, {
        headers,
      });
      toast.info("Tiempo eliminado");
      fetchTicket();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-2xl p-8 flex items-center gap-3">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-gray-700">Cargando ticket...</span>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const totalMinutos =
    ticket.timeEntries?.reduce((acc, e) => acc + (e.minutos || 0), 0) || 0;
  const totalCosto =
    ticket.timeEntries?.reduce((acc, e) => acc + (e.subtotal || 0), 0) || 0;
  const estadoOptions = ESTADOS.map((estado) => ({
    _id: estado,
    name: estado,
  }));
  const tipoActividadOptions = TIPOS_ACTIVIDAD.map((tipo) => ({
    _id: tipo,
    name: tipo,
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start gap-3">
            <div
              className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${ESTADO_COLOR[ticket.estado] || "bg-gray-400"}`}
            />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-blue-700 font-bold text-sm">
                  {ticket.codigo}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORIDAD_COLOR[ticket.prioridad]}`}
                >
                  {ticket.prioridad}
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {ticket.tipo}
                </span>
              </div>
              <h2 className="text-base font-bold text-gray-900 mt-0.5">
                {ticket.asunto}
              </h2>
              <p className="text-xs text-gray-400">
                {ticket.cliente?.name} ·{" "}
                {new Date(ticket.createdAt).toLocaleString("es-CO")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Contenido con scroll */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-5">
            {/* Info rápida */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <InfoCard
                icon={<User className="h-4 w-4" />}
                label="Solicitante"
                value={ticket.solicitante?.nombre}
              />
              <InfoCard
                icon={<Headphones className="h-4 w-4" />}
                label="Responsable"
                value={ticket.responsable?.name || "Sin asignar"}
              />
              <InfoCard
                icon={<Clock className="h-4 w-4" />}
                label="Tiempo total"
                value={`${totalMinutos} min`}
              />
              <InfoCard
                icon={<AlertCircle className="h-4 w-4" />}
                label="Estado"
                value={ticket.estado}
              />
            </div>

            {/* Descripción */}
            {ticket.descripcion && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 mb-1">
                  Descripción
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-line">
                  {ticket.descripcion}
                </p>
              </div>
            )}

            {/* Acciones rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
              {hasPermission("soporte.editar") && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Cambiar estado
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SelectField
                        name="nuevoEstado"
                        value={nuevoEstado}
                        options={estadoOptions}
                        valueKey="_id"
                        onChange={(e) =>
                          setNuevoEstado(e.target.value || ticket.estado)
                        }
                        placeholder="Seleccionar estado"
                      />
                    </div>
                    <button
                      onClick={handleCambiarEstado}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
              {hasPermission("soporte.asignar") && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Asignar responsable
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SelectField
                        name="nuevoResponsable"
                        value={nuevoResponsable}
                        options={usuarios}
                        onChange={(e) => setNuevoResponsable(e.target.value)}
                        placeholder="Sin asignar"
                      />
                    </div>
                    <button
                      onClick={handleAsignarResponsable}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition"
                    >
                      Asignar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex gap-1">
                {[
                  {
                    id: "bitacora",
                    label: "Bitácora",
                    count: ticket.bitacora?.length,
                  },
                  {
                    id: "tiempo",
                    label: "Tiempo",
                    count: ticket.timeEntries?.length,
                  },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                      tab === t.id
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {t.label}
                    {t.count > 0 && (
                      <span className="ml-1.5 bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">
                        {t.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab: Bitácora */}
            {tab === "bitacora" && (
              <div className="space-y-3">
                {ticket.bitacora?.length === 0 && (
                  <div className="text-center py-6 text-gray-400 text-sm">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    Sin comentarios aún
                  </div>
                )}
                {ticket.bitacora?.map((entry) => (
                  <div
                    key={entry._id}
                    className={`rounded-xl p-3 border ${entry.tipo === "cliente" ? "bg-blue-50 border-blue-100" : "bg-gray-50 border-gray-100"}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-700">
                          {entry.autorNombre || entry.autor?.name}
                        </span>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded-full ${entry.tipo === "cliente" ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-600"}`}
                        >
                          {entry.tipo === "cliente"
                            ? "Visible cliente"
                            : "Interno"}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(entry.createdAt).toLocaleString("es-CO", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{entry.texto}</p>
                  </div>
                ))}

                {hasPermission("soporte.comentar") && (
                  <form
                    onSubmit={handleComentario}
                    className="border border-gray-200 rounded-xl p-3 bg-white"
                  >
                    <TextAreaField
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      placeholder="Agregar comentario..."
                      rows={2}
                      className="border-0 focus:ring-0 px-0 py-0 text-gray-700"
                    />
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-2">
                      <div className="w-52">
                        <SelectField
                          name="tipoComent"
                          value={tipoComent}
                          options={TIPOS_COMENTARIO}
                          valueKey="_id"
                          onChange={(e) =>
                            setTipoComent(e.target.value || "interno")
                          }
                          placeholder="Tipo de comentario"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={enviando || !comentario.trim()}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition disabled:opacity-50"
                      >
                        <Send className="h-3.5 w-3.5" /> Enviar
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Tab: Tiempo */}
            {tab === "tiempo" && (
              <div className="space-y-3">
                {/* Resumen */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">
                      {totalMinutos}
                    </p>
                    <p className="text-xs text-gray-500">minutos totales</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">
                      $
                      {totalCosto.toLocaleString("es-CO", {
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    <p className="text-xs text-gray-500">costo mano de obra</p>
                  </div>
                </div>

                {/* Lista */}
                {ticket.timeEntries?.length === 0 && (
                  <div className="text-center py-6 text-gray-400 text-sm">
                    <Timer className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    Sin registros de tiempo
                  </div>
                )}
                {ticket.timeEntries?.map((entry) => (
                  <div
                    key={entry._id}
                    className="flex items-start justify-between bg-gray-50 rounded-xl p-3 border border-gray-100"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-gray-700">
                          {entry.tecnicoNombre}
                        </span>
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">
                          {entry.tipoActividad}
                        </span>
                        <span className="text-xs font-bold text-blue-700">
                          {entry.minutos} min
                        </span>
                      </div>
                      {entry.observacion && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {entry.observacion}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        Tarifa: $
                        {entry.costoHoraSnapshot?.toLocaleString("es-CO")}/h ·
                        Subtotal: $
                        {entry.subtotal?.toLocaleString("es-CO", {
                          maximumFractionDigits: 0,
                        })}
                      </p>
                    </div>
                    {hasPermission("soporte.editar") && (
                      <button
                        onClick={() => handleEliminarEntry(entry._id)}
                        className="text-red-400 hover:text-red-600 ml-2 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}

                {/* Formulario nuevo time entry */}
                {hasPermission("soporte.editar") && (
                  <form
                    onSubmit={handleTimeEntry}
                    className="border border-dashed border-blue-300 rounded-xl p-4 bg-blue-50 space-y-3"
                  >
                    <p className="text-xs font-semibold text-blue-700">
                      + Registrar tiempo
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <InputField
                          label="Minutos trabajados"
                          type="number"
                          min="1"
                          value={timeForm.minutos}
                          onChange={(e) =>
                            setTimeForm((p) => ({
                              ...p,
                              minutos: e.target.value,
                            }))
                          }
                          placeholder="ej. 60"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Tipo de actividad
                        </label>
                        <SelectField
                          name="tipoActividad"
                          value={timeForm.tipoActividad}
                          options={tipoActividadOptions}
                          valueKey="_id"
                          onChange={(e) =>
                            setTimeForm((p) => ({
                              ...p,
                              tipoActividad: e.target.value || "Diagnóstico",
                            }))
                          }
                          placeholder="Tipo de actividad"
                        />
                      </div>
                    </div>
                    <InputField
                      type="text"
                      value={timeForm.observacion}
                      onChange={(e) =>
                        setTimeForm((p) => ({
                          ...p,
                          observacion: e.target.value,
                        }))
                      }
                      placeholder="Observación (opcional)"
                    />
                    <button
                      type="submit"
                      disabled={registrandoTiempo}
                      className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4" />{" "}
                      {registrandoTiempo ? "Registrando..." : "Registrar"}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
      <div className="flex items-center gap-1.5 text-gray-400 mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className="text-sm font-semibold text-gray-800 truncate">
        {value || "—"}
      </p>
    </div>
  );
}
